/* ============================================
   AnnotationsPanel — List of Article Annotations
   Shows all highlights with notes, scroll-to,
   and delete functionality.
   ============================================ */

import type { WikiAnotacion } from '@/hooks/useWikiAnnotations'

/* ── Icons ── */
const icons = {
  bookmark: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  ),
  trash: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  ),
  navigate: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
}

interface AnnotationsPanelProps {
  anotaciones: WikiAnotacion[]
  onClickAnnotation: (id: string) => void
  onDeleteAnnotation: (id: string) => void
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-PE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AnnotationsPanel({
  anotaciones,
  onClickAnnotation,
  onDeleteAnnotation,
}: AnnotationsPanelProps) {
  return (
    <aside className="wiki-annotations-panel">
      {/* ── Header ── */}
      <div className="wiki-annotations-panel__header">
        <span className="wiki-annotations-panel__title">
          {icons.bookmark()} Anotaciones
        </span>
        <span className="wiki-annotations-panel__count">
          {anotaciones.length}
        </span>
      </div>

      {/* ── Annotation List ── */}
      <div className="wiki-annotations-panel__list">
        {anotaciones.length === 0 ? (
          <div className="wiki-annotations-panel__empty">
            <p>No hay anotaciones</p>
            <p className="wiki-annotations-panel__empty-hint">
              Selecciona texto en el artículo para crear una anotación
            </p>
          </div>
        ) : (
          anotaciones.map(ann => (
            <div
              key={ann.id}
              className="wiki-annotations-panel__item"
              style={{ borderLeftColor: ann.color }}
              onClick={() => onClickAnnotation(ann.id)}
            >
              {/* Selected text excerpt */}
              <div className="wiki-annotations-panel__text">
                <span
                  className="wiki-annotations-panel__highlight"
                  style={{ backgroundColor: `${ann.color}25` }}
                >
                  "{ann.texto_seleccionado.length > 80
                    ? ann.texto_seleccionado.substring(0, 80) + '...'
                    : ann.texto_seleccionado}"
                </span>
              </div>

              {/* Note */}
              {ann.nota && (
                <div className="wiki-annotations-panel__note">
                  {ann.nota}
                </div>
              )}

              {/* Footer */}
              <div className="wiki-annotations-panel__footer">
                <span className="wiki-annotations-panel__date">
                  {formatDate(ann.created_at)}
                </span>
                <div className="wiki-annotations-panel__actions">
                  <button
                    className="wiki-annotations-panel__action-btn"
                    onClick={e => {
                      e.stopPropagation()
                      onClickAnnotation(ann.id)
                    }}
                    title="Ir a la anotación"
                  >
                    {icons.navigate()}
                  </button>
                  <button
                    className="wiki-annotations-panel__action-btn wiki-annotations-panel__action-btn--danger"
                    onClick={e => {
                      e.stopPropagation()
                      onDeleteAnnotation(ann.id)
                    }}
                    title="Eliminar anotación"
                  >
                    {icons.trash()}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}
